// @ts-nocheck
import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useHotelPartnerId } from './hooks/useHotelPartnerId';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import Papa from 'papaparse';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RoomBlockUpload } from './RoomBlockUpload';
import { StaybookUpload } from './StaybookUpload';

interface ParsedRow {
  row: number;
  coach_name: string;
  coach_email: string;
  team_name: string;
  hotel_name: string;
  confirmation_number: string;
  check_in_date: string;
  check_out_date: string;
  number_of_rooms: number;
  rate_per_night: number;
  status: 'valid' | 'warning' | 'error';
  errors: string[];
  warnings: string[];
  registration_id?: string;
  hotel_property_id?: string;
}

const REQUIRED_HEADERS = ['coach_email', 'team_name', 'hotel_name', 'confirmation_number', 'check_in_date', 'check_out_date', 'number_of_rooms', 'rate_per_night'];

const HotelUpload: React.FC = () => {
  const { user } = useAuth();
  const { data: partnerId } = useHotelPartnerId();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState('bookings');

  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; updated: number; skipped: number } | null>(null);

  // Assigned events
  const { data: events } = useQuery({
    queryKey: ['hotel-upload-events', partnerId],
    enabled: !!partnerId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('event_id, events!inner(name)')
        .eq('hotel_partner_user_id', partnerId);
      if (error) throw error;
      return (data || []).map((d: any) => ({ id: d.event_id, name: d.events?.name || '' }));
    },
  });

  // Event teams for validation (from the tracker view)
  const { data: eventTeams } = useQuery({
    queryKey: ['hotel-upload-teams', selectedEventId],
    enabled: !!selectedEventId && !!partnerId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_hotel_team_tracker')
        .select('registration_id, coach_email, team_name')
        .eq('event_id', selectedEventId);
      if (error) throw error;
      return data || [];
    },
  });

  // Event hotels for property matching
  const { data: eventHotels } = useQuery({
    queryKey: ['hotel-upload-hotels', selectedEventId],
    enabled: !!selectedEventId && !!partnerId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_properties')
        .select('id, hotel_name, commission_pct, rebate_per_room')
        .eq('event_id', selectedEventId);
      if (error) throw error;
      return data || [];
    },
  });

  const validateRow = (row: any, idx: number): ParsedRow => {
    const errors: string[] = [];
    const warnings: string[] = [];

    const coach_email = (row.coach_email || '').trim().toLowerCase();
    const team_name = (row.team_name || '').trim();
    const hotel_name = (row.hotel_name || '').trim();
    const confirmation_number = (row.confirmation_number || '').trim();
    const check_in_date = (row.check_in_date || '').trim();
    const check_out_date = (row.check_out_date || '').trim();
    const number_of_rooms = parseInt(row.number_of_rooms || '0');
    const rate_per_night = parseFloat(row.rate_per_night || '0');

    if (!coach_email) errors.push('Missing coach_email');
    if (!team_name) errors.push('Missing team_name');
    if (!hotel_name) errors.push('Missing hotel_name');
    if (!confirmation_number) errors.push('Missing confirmation_number');
    if (!check_in_date) errors.push('Missing check_in_date');
    if (!check_out_date) errors.push('Missing check_out_date');
    if (isNaN(number_of_rooms) || number_of_rooms <= 0) errors.push('Invalid number_of_rooms');
    if (isNaN(rate_per_night) || rate_per_night <= 0) errors.push('Invalid rate_per_night');

    // Match registration
    let registration_id: string | undefined;
    if (coach_email && eventTeams) {
      const match = eventTeams.find((t: any) => t.coach_email?.toLowerCase() === coach_email);
      if (match) {
        registration_id = match.registration_id;
      } else {
        warnings.push('Coach email not found in event registrations');
      }
    }

    // Match hotel property
    let hotel_property_id: string | undefined;
    if (hotel_name && eventHotels) {
      const hotelMatch = eventHotels.find((h: any) => h.hotel_name?.toLowerCase() === hotel_name.toLowerCase());
      if (hotelMatch) {
        hotel_property_id = hotelMatch.id;
      } else {
        warnings.push('Hotel not in contracted hotels list');
      }
    }

    // Duplicate check is done at import time (update path)

    return {
      row: idx + 2, // 1-indexed + header
      coach_name: (row.coach_name || '').trim(),
      coach_email,
      team_name,
      hotel_name,
      confirmation_number,
      check_in_date,
      check_out_date,
      number_of_rooms,
      rate_per_night,
      status: errors.length > 0 ? 'error' : warnings.length > 0 ? 'warning' : 'valid',
      errors,
      warnings,
      registration_id,
      hotel_property_id,
    };
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setImportResult(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields || [];
        const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
        if (missing.length > 0) {
          toast.error(`Missing required columns: ${missing.join(', ')}`);
          setParsedRows([]);
          return;
        }

        const rows = (results.data as any[]).map((row, idx) => validateRow(row, idx));
        setParsedRows(rows);
      },
      error: () => toast.error('Failed to parse CSV file'),
    });

    if (fileRef.current) fileRef.current.value = '';
  };

  const handleImport = async () => {
    if (!selectedEventId || !user) return;
    const validRows = parsedRows.filter((r) => r.status !== 'error' && r.registration_id);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    let imported = 0;
    let updated = 0;
    let skipped = 0;

    for (const row of validRows) {
      try {
        // Check for existing booking with same registration + confirmation
        const { data: existing } = await (supabase as any)
          .from('hotel_bookings')
          .select('id')
          .eq('registration_id', row.registration_id)
          .eq('confirmation_number', row.confirmation_number)
          .maybeSingle();

        // room_nights / gross_revenue / commission_earned / rebate_earned are
        // trigger-computed — never sent from the client.
        const bookingData = {
          registration_id: row.registration_id,
          event_id: selectedEventId,
          hotel_property_id: row.hotel_property_id || null,
          hotel_name: row.hotel_name,
          confirmation_number: row.confirmation_number,
          check_in_date: row.check_in_date,
          check_out_date: row.check_out_date,
          number_of_rooms: row.number_of_rooms,
          rate_per_night: row.rate_per_night,
          status: 'confirmed',
          entered_by: user.id,
          coach_email: row.coach_email,
          team_name: row.team_name,
        };

        if (existing) {
          await (supabase as any).from('hotel_bookings').update(bookingData).eq('id', existing.id);
          updated++;
        } else {
          await (supabase as any).from('hotel_bookings').insert(bookingData);
          imported++;
        }
      } catch {
        skipped++;
      }
    }

    setImportResult({ imported, updated, skipped });
    toast.success(`Import complete: ${imported} new, ${updated} updated`);
    qc.invalidateQueries({ queryKey: ['hotel-team-list'] });
    qc.invalidateQueries({ queryKey: ['hotel-event-header'] });
    setImporting(false);
  };

  const validCount = parsedRows.filter((r) => r.status === 'valid').length;
  const warnCount = parsedRows.filter((r) => r.status === 'warning').length;
  const errorCount = parsedRows.filter((r) => r.status === 'error').length;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Booking Upload</h1>

      {/* Event selector */}
      <div className="max-w-sm">
        <Select value={selectedEventId} onValueChange={id => { setSelectedEventId(id); setParsedRows([]); setFileName(''); setImportResult(null); }}>
          <SelectTrigger>
            <SelectValue placeholder="Select an event..." />
          </SelectTrigger>
          <SelectContent>
            {(events || []).map((ev: any) => (
              <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={uploadType} onValueChange={setUploadType}><TabsList className="h-auto flex flex-wrap justify-start">
        <TabsTrigger value="bookings" className="min-h-11">Bookings CSV</TabsTrigger>
        <TabsTrigger value="room-blocks" className="min-h-11">Rooms On Hold report (TBD)</TabsTrigger>
        <TabsTrigger value="staybook" className="min-h-11">Staybook pickup report</TabsTrigger>
      </TabsList></Tabs>

      {selectedEventId && uploadType === 'room-blocks' && <RoomBlockUpload key={selectedEventId} eventId={selectedEventId} teams={eventTeams || []} />}
      {selectedEventId && uploadType === 'staybook' && <StaybookUpload key={selectedEventId} eventId={selectedEventId} teams={eventTeams || []} hotels={eventHotels || []} />}
      {selectedEventId && uploadType === 'bookings' && (
        <>
          {/* Upload area */}
          <Card
            className="border-dashed border-2 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            <CardContent className="py-12 text-center">
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">
                {fileName ? `Selected: ${fileName}` : 'Click to upload a CSV file'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Required columns: {REQUIRED_HEADERS.join(', ')}
              </p>
            </CardContent>
          </Card>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />

          {/* Preview table */}
          {parsedRows.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-3">
                  Preview ({parsedRows.length} rows)
                  <Badge variant="default">{validCount} valid</Badge>
                  {warnCount > 0 && <Badge variant="secondary">{warnCount} warnings</Badge>}
                  {errorCount > 0 && <Badge variant="destructive">{errorCount} errors</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Row</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Coach Email</TableHead>
                      <TableHead>Hotel</TableHead>
                      <TableHead>Conf#</TableHead>
                      <TableHead>Rooms</TableHead>
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.map((row) => (
                      <TableRow key={row.row}>
                        <TableCell className="text-xs">{row.row}</TableCell>
                        <TableCell>
                          {row.status === 'valid' && <CheckCircle className="h-4 w-4 text-green-600" />}
                          {row.status === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-600" />}
                          {row.status === 'error' && <XCircle className="h-4 w-4 text-red-600" />}
                        </TableCell>
                        <TableCell className="text-sm">{row.team_name}</TableCell>
                        <TableCell className="text-sm">{row.coach_email}</TableCell>
                        <TableCell className="text-sm">{row.hotel_name}</TableCell>
                        <TableCell className="text-sm">{row.confirmation_number}</TableCell>
                        <TableCell className="text-sm">{row.number_of_rooms}</TableCell>
                        <TableCell className="text-xs">
                          {row.errors.map((e) => (
                            <span key={e} className="text-red-600 block">{e}</span>
                          ))}
                          {row.warnings.map((w) => (
                            <span key={w} className="text-yellow-600 block">{w}</span>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Import button */}
          {parsedRows.length > 0 && (
            <div className="flex items-center gap-3">
              <Button onClick={handleImport} disabled={importing || validCount + warnCount === 0}>
                {importing ? 'Importing...' : `Import ${validCount + warnCount} Booking(s)`}
              </Button>
              {importResult && (
                <p className="text-sm text-muted-foreground">
                  {importResult.imported} imported, {importResult.updated} updated, {importResult.skipped} skipped
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HotelUpload;
